import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import moment from 'moment';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import { getStatusColor } from '../../utils/calendarUtils';
import { normalizeShiftType } from '../../utils/shiftTypeUtils';

// Minimal shape we consume from GET /shift/myShifts.
// The response is loose - we only touch the fields we render.
export interface MyShiftRow {
    requestId?: number | string;
    requestSeqId?: number | string;
    date?: string;
    requestDt?: string;
    requestHours?: number;
    startTime?: string;
    endTime?: string;
    shiftType?: string;
    requestType?: string;
    title?: string;
    requestStatus?: 'APP' | 'PND' | 'PST' | 'DEN' | 'CAN';
    skill?: string;
    skillDesc?: string;
    requiredSkillDesc?: string;
    requestedSkillDesc?: string;
}

interface WorkerWeekScheduleProps {
    userId: string;
    userName: string;
    siteCd: string;
    /** Date of the shift being reviewed - week is anchored around this. */
    anchorDate: Date;
    /**
     * Ids (requestId-requestSeqId, or gig-requestId-requestSeqId) of the
     * shift(s) currently shown in the details dialog. Used to disable the
     * click on just that specific shift in the preview, rather than the
     * entire day - any other shift on the same day should still be
     * clickable.
     */
    currentShiftIds?: string[];
    /** Optional callback fired when a shift is clicked. */
    onDayClick?: (dayShifts: MyShiftRow[], date: Date) => void;
}

const shiftId = (s: MyShiftRow): string => {
    const isGig = (s.shiftType || s.requestType) === 'GIG';
    return isGig ? `gig-${s.requestId}-${s.requestSeqId}` : `${s.requestId}-${s.requestSeqId}`;
};

const STATUS_LABELS: Record<string, string> = {
    APP: 'Approved',
    PND: 'Pending',
    PST: 'Posted',
    DEN: 'Rejected',
    CAN: 'Cancelled',
};

// Map API codes to the label bucket that calendarUtils.getStatusColor uses.
const statusCodeToLabel = (code?: string): 'APPROVED' | 'PENDING APPROVAL' | 'POSTED' | 'REJECTED' | 'CANCELLED' => {
    switch (code) {
        case 'APP': return 'APPROVED';
        case 'PND': return 'PENDING APPROVAL';
        case 'PST': return 'POSTED';
        case 'CAN': return 'CANCELLED';
        case 'DEN':
        default: return 'REJECTED';
    }
};

const formatTime = (time24?: string): string => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hours12 = h % 12 || 12;
    return `${hours12}:${(m || 0).toString().padStart(2, '0')} ${period}`;
};

const WorkerWeekSchedule: React.FC<WorkerWeekScheduleProps> = ({ userId, userName, siteCd, anchorDate, currentShiftIds, onDayClick }) => {
    const [shifts, setShifts] = useState<MyShiftRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Offset (in weeks) from the anchor week, controlled by nav buttons.
    const [weekOffset, setWeekOffset] = useState(0);

    // Sun -> Sat week around the anchor date, shifted by weekOffset.
    const weekStart = useMemo(
        () => moment(anchorDate).startOf('week').add(weekOffset, 'weeks'),
        [anchorDate, weekOffset]
    );
    const weekDays = useMemo(
        () => Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'days')),
        [weekStart]
    );

    useEffect(() => {
        let cancelled = false;
        if (!userId || !siteCd) return;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                // Both endpoints now support bounding results to a date range,
                // so scope the fetch to a window around the visible week -
                // this refetches as the user pages further out via the
                // prev/next week chevrons instead of pulling full history.
                const rangeStart = weekStart.clone().subtract(4, 'weeks').format('YYYY-MM-DD');
                const rangeEnd = weekStart.clone().add(6, 'days').add(4, 'weeks').format('YYYY-MM-DD');

                const params = new URLSearchParams({
                    filter: 'range',
                    startDate: rangeStart,
                    endDate: rangeEnd,
                });
                const gigParams = new URLSearchParams({
                    siteCd,
                    userId,
                    statusFilter: 'ALL',
                    filter: 'all',
                    startDate: rangeStart,
                    endDate: rangeEnd,
                });
                const [regularResult, gigResult] = await Promise.allSettled([
                    API_FETCH(`${ENDPOINTS.GET_MY_SHIFTS}/${siteCd}/${userId}?${params.toString()}`),
                    API_FETCH(`${ENDPOINTS.GIG_GET_REQUESTS}?${gigParams.toString()}`),
                ]);
                if (cancelled) return;

                const regularRows: MyShiftRow[] = regularResult.status === 'fulfilled'
                    ? (Array.isArray(regularResult.value) ? regularResult.value : (regularResult.value?.shifts ?? []))
                    : [];
                const gigDetails = gigResult.status === 'fulfilled'
                    ? (Array.isArray(gigResult.value) ? gigResult.value : (gigResult.value?.details ?? []))
                    : [];
                const gigRows: MyShiftRow[] = gigDetails.map((g: any) => ({
                    requestId: g.requestId,
                    requestSeqId: g.requestSeqId,
                    requestDt: g.requestDt,
                    requestHours: g.requestHours,
                    shiftType: 'GIG',
                    requestStatus: g.requestStatus,
                    skillDesc: g.requestedSkillDesc ?? g.requestedSkillCd,
                    requestedSkillDesc: g.requestedSkillDesc,
                }));

                if (regularResult.status === 'rejected' && gigResult.status === 'rejected') {
                    throw new Error('Failed to load worker schedule');
                }

                setShifts(
                    [...regularRows, ...gigRows].sort((a, b) => {
                        const aDate = moment(a.date ?? a.requestDt).valueOf();
                        const bDate = moment(b.date ?? b.requestDt).valueOf();
                        return aDate - bDate;
                    })
                );
            } catch (err: any) {
                if (cancelled) return;
                setError(err?.message || 'Failed to load worker schedule');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [userId, siteCd, weekStart]);

    // Reset to anchor week whenever the anchor changes (new shift selected).
    useEffect(() => {
        setWeekOffset(0);
    }, [anchorDate]);

    // Group shifts by yyyy-MM-dd for the anchor week
    const shiftsByDay = useMemo(() => {
        const map: Record<string, MyShiftRow[]> = {};
        const start = weekStart.clone().startOf('day');
        const end = weekStart.clone().add(6, 'days').endOf('day');
        shifts.forEach((s) => {
            const rawDate = s.date ?? s.requestDt;
            if (!rawDate) return;
            const d = moment(rawDate);
            if (d.isBefore(start) || d.isAfter(end)) return;
            const key = d.format('YYYY-MM-DD');
            if (!map[key]) map[key] = [];
            map[key].push(s);
        });
        return map;
    }, [shifts, weekStart]);

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {`${userName}'s Week`} ({weekStart.format('MMM D')} - {weekStart.clone().add(6, 'days').format('MMM D, YYYY')})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => setWeekOffset((o) => o - 1)} aria-label="Previous week">
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    {weekOffset !== 0 && (
                        <Typography
                            variant="caption"
                            onClick={() => setWeekOffset(0)}
                            sx={{ cursor: 'pointer', color: '#CC0000', fontWeight: 600 }}
                        >
                            Today
                        </Typography>
                    )}
                    <IconButton size="small" onClick={() => setWeekOffset((o) => o + 1)} aria-label="Next week">
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}
            {error && !loading && (
                <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>
            )}

            {!loading && !error && (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        p: 1,
                        backgroundColor: '#fafafa',
                        minWidth: 'fit-content',
                    }}>
                    {weekDays.map((day) => {
                        const key = day.format('YYYY-MM-DD');
                        const daysShifts = shiftsByDay[key] || [];
                        const isAnchor = day.isSame(anchorDate, 'day');
                        return (
                            <Box
                                key={key}
                                sx={{
                                minHeight: 90,
                                // On wide screens keep every day the same size; when things get
                                // tight, let empty days shrink so days with shifts can breathe.
                                flex: {
                                    xs: daysShifts.length > 0 ? '1 1 110px' : '0 1 60px',
                                    md: '1 1 0',
                                },
                                minWidth: {
                                    xs: daysShifts.length > 0 ? 110 : 60,
                                    md: 0,
                                },
                                p: 0.75,
                                border: isAnchor ? '2px solid #CC0000' : '1px solid #e0e0e0',
                                borderRadius: '4px',
                                backgroundColor: '#fff',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                overflow: 'hidden',
                            }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 0.25 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>
                                        {day.format('ddd')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: isAnchor ? '#CC0000' : '#333' }}>
                                        {day.format('D')}
                                    </Typography>
                                </Box>
                                {daysShifts.length === 0 && (
                                    <Typography variant="caption" sx={{ color: '#bbb' }}>
                                        -
                                    </Typography>
                                )}
                                {daysShifts.map((s, idx) => {
                                    const label = STATUS_LABELS[s.requestStatus || ''] || s.requestStatus || '';
                                    const color = getStatusColor(statusCodeToLabel(s.requestStatus));
                                    const skill = s.requiredSkillDesc || s.skillDesc || s.requestedSkillDesc || s.skill;
                                    const typeLabel = normalizeShiftType(s.shiftType || s.requestType);
                                    const timeLabel = s.requestHours != null
                                        ? `${s.requestHours} hrs`
                                        : `${formatTime(s.startTime)}${s.endTime ? ` - ${formatTime(s.endTime)}` : ''}`;
                                    // Each shift is individually clickable (except the shift that's
                                    // already the currently-shown shift), so with multiple shifts
                                    // on one day - including the anchor day itself - the user can
                                    // navigate to any of the others.
                                    const isCurrentShift = !!currentShiftIds?.includes(shiftId(s));
                                    const isShiftClickable = !!onDayClick && !isCurrentShift;
                                    return (
                                        <Box
                                            key={`${s.requestId}-${s.requestSeqId}-${idx}`}
                                            title={`${timeLabel}${skill ? ` - ${skill}` : ''} (${label})`}
                                            onClick={isShiftClickable ? () => onDayClick!([s], day.toDate()) : undefined}
                                            sx={{
                                                backgroundColor: color,
                                                color: 'black',
                                                opacity: 0.95,
                                                borderRadius: '4px',
                                                px: 0.5,
                                                py: 0.25,
                                                overflow: 'hidden',
                                                cursor: isShiftClickable ? 'pointer' : 'default',
                                                transition: 'box-shadow 0.15s, border-color 0.15s',
                                                ...(isShiftClickable && {
                                                    '&:hover': {
                                                        boxShadow: '0 0 0 2px rgba(204, 0, 0, 0.3)',
                                                    },
                                                }),
                                            }}
                                        >
                                            {(typeLabel || skill) && (
                                                <Typography variant="caption" sx={{
                                                    display: 'block',
                                                    fontSize: '0.65rem',
                                                    lineHeight: 1.2,
                                                    fontWeight: 700,
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                }}>
                                                    {[typeLabel, skill].filter(Boolean).join(' - ')}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                                                {timeLabel}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default WorkerWeekSchedule;
